/**
 * timezone -- shared America/Chicago day-boundary helpers.
 *
 * Several src/ui primitives (Countdown, Runway) need to reason about "today"
 * or a bare calendar date (no time component) in Joseph's local timezone
 * rather than the browser's/server's timezone or raw UTC. This module wraps
 * the standard Intl-based trick for computing a timezone's offset at a given
 * instant, with no extra dependency.
 */

const CT_TIMEZONE = 'America/Chicago';

export interface YMD {
  year: number;
  month: number; // 1-12
  day: number;
}

/** Minutes to ADD to a UTC instant to get Chicago wall-clock time (negative
 *  during CDT/CST, varies with DST -- this is why it must be computed per
 *  instant rather than hardcoded). */
export function chicagoOffsetMinutes(utcInstant: Date): number {
  const parts = formatPartsInChicago(utcInstant);
  const asUtcMillis = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return (asUtcMillis - utcInstant.getTime()) / 60000;
}

function formatPartsInChicago(utcInstant: Date): YMD & { hour: number; minute: number; second: number } {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: CT_TIMEZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const map: Record<string, string> = {};
  for (const p of dtf.formatToParts(utcInstant)) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

/** The Y/M/D of an instant, as seen on a Chicago wall clock. */
export function chicagoYMD(utcInstant: Date): YMD {
  const { year, month, day } = formatPartsInChicago(utcInstant);
  return { year, month, day };
}

/** "Today" as a Y/M/D triple on the Chicago wall clock. */
export function chicagoToday(now: Date = new Date()): YMD {
  return chicagoYMD(now);
}

/** Convert a Chicago wall-clock Y-M-D + H:m:s into the UTC instant it represents. */
export function chicagoWallTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
): Date {
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const offset = chicagoOffsetMinutes(utcGuess);
  return new Date(utcGuess.getTime() - offset * 60000);
}

/**
 * Parse an ISO date/instant string. A bare "YYYY-MM-DD" date has no inherent
 * timezone, so it is anchored to the START or END of that calendar day in
 * Chicago (per `edge`); a full instant (with a time component, "Z", or a
 * numeric offset) parses as-is via the native Date constructor.
 */
export function parseIsoAnchoredToChicago(iso: string, edge: 'start' | 'end' = 'start'): Date {
  const bareDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (bareDateMatch) {
    const [, y, m, d] = bareDateMatch;
    return edge === 'end'
      ? chicagoWallTimeToUtc(Number(y), Number(m), Number(d), 23, 59, 59)
      : chicagoWallTimeToUtc(Number(y), Number(m), Number(d), 0, 0, 0);
  }
  return new Date(iso);
}

/** Compare two Y/M/D triples: negative if a<b, 0 if equal, positive if a>b. */
export function compareYMD(a: YMD, b: YMD): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}
